// DE-BRIEFS: the brief rhythm, one quick-edit modal for its three levels — the company's defaults
// and the Chief of Staff report (Setup › Chief of Staff), a person's for the briefs they receive
// (Account › Notifications), a DE's with its recipients (the DE page). Each level stores only what
// it changes: a field shows "Default" while it inherits, and "Reset to default" sends all nulls.
// `briefSettings.*` (packages/schema/src/contracts/api/de-briefs.ts); the rules are the server's.
import type { OperationInput, OperationOutput } from "@companyos/sdk";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InfoTip,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Segmented,
  SettingsRow,
  Switch,
  Toggle,
  toast,
  workerName,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilIcon } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { BriefRules } from "./brief-rules";

export type BriefView = OperationOutput<"briefSettings.getCompany">;
type Schedule = BriefView["effective"];
type Frequency = Schedule["frequency"];
type Put = OperationInput<"briefSettings.putCompany">["body"];

export type BriefTarget =
  | { scope: "company" }
  | { scope: "person" }
  | { scope: "de"; id: string; name: string };

export const briefKey = (co: string, t: BriefTarget) =>
  ["company", co, "brief-settings", t.scope, t.scope === "de" ? t.id : null] as const;

export const briefQuery = (api: Api, co: string, t: BriefTarget) =>
  queryOptions({
    queryKey: briefKey(co, t),
    queryFn: () =>
      t.scope === "company"
        ? api.client.briefSettings.getCompany({ params: { co } })
        : t.scope === "person"
          ? api.client.briefSettings.getMine({ params: { co } })
          : api.client.briefSettings.getDe({ params: { co, id: t.id } }),
  });

const DAYS = [
  { n: 1, short: "Mon" },
  { n: 2, short: "Tue" },
  { n: 3, short: "Wed" },
  { n: 4, short: "Thu" },
  { n: 5, short: "Fri" },
  { n: 6, short: "Sat" },
  { n: 7, short: "Sun" },
] as const;
const dayName = (n: number) => DAYS.find((d) => d.n === n)?.short ?? "";

const FREQUENCIES: { value: Frequency; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "custom", label: "Custom" },
];

/** "Mon–Fri 08:00", "Fri 16:00", "Mon, Wed 09:30", or "Off". */
export function briefSummary(s: Schedule): string {
  if (!s.enabled) return "Off";
  const days =
    s.frequency === "daily"
      ? "Mon–Fri"
      : s.frequency === "weekly"
        ? dayName(s.days[0] ?? 1)
        : [...new Set(s.days)]
            .sort((a, b) => a - b)
            .map(dayName)
            .join(", ");
  return `${days} ${s.time}`;
}

/** "Fri 08:00" in the company's zone. */
export function nextLabel(iso: string, zone: string): string {
  const at = new Date(iso);
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(at);
    const p = (t: string) => parts.find((x) => x.type === t)?.value ?? "";
    return `${p("weekday")} ${p("hour")}:${p("minute")}`;
  } catch {
    return at.toLocaleString();
  }
}

type Draft = {
  enabled: boolean | null;
  frequency: Frequency | null;
  days: number[] | null;
  time: string | null;
  recipients: string[] | null;
  cos: string | null;
};

const draftOf = (v: BriefView): Draft => ({
  ...v.setting,
  recipients: v.recipients_set ? v.recipients.map((r) => r.membership_id) : null,
  cos: v.cos?.set ? v.cos.membership_id : null,
});

function bodyOf(scope: BriefTarget["scope"], d: Draft): Put {
  return {
    enabled: d.enabled,
    frequency: d.frequency,
    days: d.days,
    time: d.time,
    ...(scope === "de" ? { recipients: d.recipients?.length ? d.recipients : null } : {}),
    ...(scope === "company" ? { cos_membership_id: d.cos } : {}),
  };
}

const TITLES = { company: "Company briefs", person: "My briefs" } as const;

/** The brief settings of one level, in a modal; read-only when the view says so. */
export function BriefSettingsModal({
  api,
  co,
  target,
  onClose,
}: {
  api: Api;
  co: string;
  target: BriefTarget;
  onClose: () => void;
}) {
  const q = useQuery(briefQuery(api, co, target));
  const title = target.scope === "de" ? `${workerName(target.name)}'s brief` : TITLES[target.scope];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px]" data-slot="brief-settings">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            {title}
            <InfoTip label="About briefs">
              {target.scope === "company"
                ? "Every DE sends the person it reports to a short brief; the Chief of Staff sends the CEO one report. These are the company's defaults."
                : target.scope === "person"
                  ? "How often the DEs reporting to you brief you. Applies to every DE unless one is set on its own."
                  : "What this DE is working on, sent to its recipients. A reply corrects it."}
            </InfoTip>
          </DialogTitle>
        </DialogHeader>
        {q.isPending ? (
          <Loading label="Loading the brief settings" rows={4} />
        ) : q.isError ? (
          <LoadError what="The brief settings" error={q.error} />
        ) : (
          <BriefForm
            key={JSON.stringify(q.data.setting)}
            api={api}
            co={co}
            target={target}
            view={q.data}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** A field's label, with "Default" beside it while the level inherits it. */
function FieldHead({
  htmlFor,
  label,
  inherited,
  tip,
}: {
  htmlFor?: string;
  label: string;
  inherited: boolean;
  tip?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {htmlFor ? (
        <Label htmlFor={htmlFor}>{label}</Label>
      ) : (
        <span className="text-sm font-medium">{label}</span>
      )}
      {tip ? <InfoTip label={`About ${label}`}>{tip}</InfoTip> : null}
      {inherited ? (
        <span className="text-xs text-muted-foreground" data-source="default">
          Default
        </span>
      ) : null}
    </div>
  );
}

function BriefForm({
  api,
  co,
  target,
  view,
  onClose,
}: {
  api: Api;
  co: string;
  target: BriefTarget;
  view: BriefView;
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => draftOf(view));
  const ro = !view.can_edit;
  const save = useMutation({
    mutationFn: (body: Put) =>
      target.scope === "company"
        ? api.client.briefSettings.putCompany({ params: { co }, body })
        : target.scope === "person"
          ? api.client.briefSettings.putMine({ params: { co }, body })
          : api.client.briefSettings.putDe({ params: { co, id: target.id }, body }),
    onSuccess: (v) => {
      qc.setQueryData(briefKey(co, target), v);
      // The other levels resolve over this one.
      void qc.invalidateQueries({ queryKey: ["company", co, "brief-settings"] });
      toast.add({ title: "Saved", type: "success" });
      onClose();
    },
  });
  const set = (patch: Partial<Draft>) => {
    save.reset();
    setDraft((d) => ({ ...d, ...patch }));
  };

  const inh = view.inherited;
  const enabled = inh.enabled && (draft.enabled ?? inh.enabled);
  const frequency = draft.frequency ?? inh.frequency;
  const days = draft.days ?? inh.days;
  const time = draft.time ?? inh.time;
  const dirty = JSON.stringify(draft) !== JSON.stringify(draftOf(view));
  const isDefault = Object.values(draftOf(view)).every((v) => v === null);

  const pickFrequency = (f: Frequency) =>
    set({
      frequency: f,
      days: f === "weekly" ? [days[0] ?? 5] : f === "custom" ? [...days] : draft.days,
    });
  const toggleDay = (n: number, on: boolean) => {
    if (frequency === "weekly") {
      if (on) set({ days: [n] });
      return;
    }
    const next = on ? [...new Set([...days, n])] : days.filter((d) => d !== n);
    if (next.length) set({ days: next.sort((a, b) => a - b) });
  };
  const shownDays = frequency === "daily" ? [1, 2, 3, 4, 5] : days;

  return (
    <form
      className="flex flex-col gap-4"
      aria-label="Brief settings"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ro && dirty) save.mutate(bodyOf(target.scope, draft));
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Switch
            id={`${id}-on`}
            checked={enabled}
            disabled={ro || !inh.enabled}
            onCheckedChange={(on) => set({ enabled: on })}
          />
          <Label htmlFor={`${id}-on`}>{enabled ? "On" : "Off"}</Label>
          {!inh.enabled ? (
            <InfoTip label="About Off">Turned off at a higher level.</InfoTip>
          ) : draft.enabled === null ? (
            <span className="text-xs text-muted-foreground" data-source="default">
              Default
            </span>
          ) : null}
        </div>
        <span className="text-xs text-muted-foreground" data-slot="brief-next">
          {view.next_at ? `Next: ${nextLabel(view.next_at, view.time_zone)}` : "No brief planned"}
        </span>
      </div>

      <fieldset className="flex flex-col gap-2" disabled={ro || !enabled}>
        <FieldHead label="Frequency" inherited={draft.frequency === null} />
        <Segmented
          aria-label="Frequency"
          size="sm"
          options={FREQUENCIES.map((f) => ({
            value: f.value,
            label: f.value === "daily" ? "Daily (Mon–Fri)" : f.label,
          }))}
          value={frequency}
          onValueChange={pickFrequency}
        />
      </fieldset>

      {frequency !== "daily" ? (
        <fieldset
          className="flex flex-col gap-2"
          aria-label={frequency === "weekly" ? "Day" : "Days"}
          disabled={ro || !enabled}
        >
          <FieldHead
            label={frequency === "weekly" ? "Day" : "Days"}
            inherited={draft.days === null}
          />
          <div className="flex flex-wrap gap-1" data-slot="brief-days">
            {DAYS.map((d) => (
              <Toggle
                key={d.n}
                variant="chip"
                size="sm"
                aria-label={d.short}
                pressed={shownDays.includes(d.n)}
                disabled={ro || !enabled}
                onPressedChange={(on) => toggleDay(d.n, on)}
              >
                {d.short}
              </Toggle>
            ))}
          </div>
        </fieldset>
      ) : null}

      <div className="flex flex-col gap-2">
        <FieldHead
          htmlFor={`${id}-time`}
          label="Time"
          inherited={draft.time === null}
          tip={`Company time (${view.time_zone}).`}
        />
        <Input
          id={`${id}-time`}
          type="time"
          className="h-9 w-32"
          required
          disabled={ro || !enabled}
          value={time}
          onChange={(e) => e.target.value && set({ time: e.target.value })}
        />
      </div>

      {target.scope === "de" ? (
        <Recipients
          view={view}
          chosen={draft.recipients}
          disabled={ro}
          onChange={(recipients) => set({ recipients })}
        />
      ) : null}

      {target.scope === "company" && view.cos ? (
        <div className="flex flex-col gap-3 border-t border-subtle pt-4" data-slot="brief-cos">
          <div className="flex flex-col gap-2">
            <FieldHead
              htmlFor={`${id}-cos`}
              label="Chief of Staff"
              inherited={draft.cos === null}
              tip="Sends the CEO one report of the whole team."
            />
            <NativeSelect
              id={`${id}-cos`}
              className="w-full"
              disabled={ro}
              value={draft.cos ?? ""}
              onChange={(e) => set({ cos: e.target.value || null })}
            >
              <NativeSelectOption value="">
                {view.cos.membership_id && !view.cos.set
                  ? `Default (${workerName(
                      view.cos.choices.find((c) => c.membership_id === view.cos?.membership_id)
                        ?.name ?? "",
                    )})`
                  : "Default"}
              </NativeSelectOption>
              {view.cos.choices.map((c) => (
                <NativeSelectOption key={c.membership_id} value={c.membership_id}>
                  {workerName(c.name)}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="font-medium">CEO</span>
            <span className="text-muted-foreground" data-slot="brief-ceo">
              {view.cos.ceo?.name ?? "No CEO seat filled"}
            </span>
          </div>
        </div>
      ) : null}

      {target.scope !== "de" ? <BriefRules api={api} co={co} /> : null}

      {save.error ? (
        <Notice tone="danger" role="alert">
          {problemText(save.error, "The brief settings could not be saved.")}
        </Notice>
      ) : null}

      <DialogFooter className="sm:justify-between">
        {ro ? (
          <span />
        ) : (
          <Button
            type="button"
            variant="ghost"
            disabled={isDefault || save.isPending}
            onClick={() =>
              save.mutate(
                bodyOf(target.scope, {
                  enabled: null,
                  frequency: null,
                  days: null,
                  time: null,
                  recipients: null,
                  cos: null,
                }),
              )
            }
          >
            Reset to default
          </Button>
        )}
        <div className="flex gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {ro ? "Close" : "Cancel"}
          </Button>
          {ro ? null : (
            <Button type="submit" disabled={!dirty || save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          )}
        </div>
      </DialogFooter>
    </form>
  );
}

/** A settings row: "Briefs", the rhythm that applies, and a visible Edit opening the modal. */
export function BriefSettingsRow({
  api,
  co,
  target,
  label = "Briefs",
}: {
  api: Api;
  co: string;
  target: BriefTarget;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const q = useQuery(briefQuery(api, co, target));
  return (
    <>
      <SettingsRow
        label={label}
        hint={q.data ? briefSummary(q.data.effective) : undefined}
        data-slot="brief-row"
      >
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-label={`Edit ${label.toLowerCase()}`}
          onClick={() => setOpen(true)}
        >
          <PencilIcon aria-hidden="true" />
          Edit
        </Button>
      </SettingsRow>
      {open ? (
        <BriefSettingsModal api={api} co={co} target={target} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

/** A DE's recipients: the reporting line by default, or the people chosen. */
function Recipients({
  view,
  chosen,
  disabled,
  onChange,
}: {
  view: BriefView;
  chosen: string[] | null;
  disabled: boolean;
  onChange: (ids: string[] | null) => void;
}) {
  const id = useId();
  const line = chosen === null;
  const lineNames = view.recipients_set ? null : view.recipients.map((r) => r.name).join(", ");
  return (
    <div className="flex flex-col gap-2" data-slot="brief-recipients">
      <FieldHead
        label="Recipients"
        inherited={line}
        tip="By default, the person this DE's seat reports to."
      />
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${id}-line`}
          checked={line}
          disabled={disabled}
          onCheckedChange={(on) =>
            onChange(on === true ? null : view.recipients.map((r) => r.membership_id))
          }
        />
        <Label htmlFor={`${id}-line`} className="font-normal">
          Reporting line{lineNames ? ` (${lineNames})` : ""}
        </Label>
      </div>
      {line && view.recipients_set ? (
        // The saved list was cleared: saving goes back to the reporting line.
        <p className="pl-6 text-xs text-muted-foreground" data-slot="brief-recipients-line">
          Will use the reporting line
        </p>
      ) : line && !lineNames ? (
        <p className="pl-6 text-xs text-muted-foreground" data-slot="brief-recipients-line">
          No one: the seat reports to no person
        </p>
      ) : null}
      {!line ? (
        view.recipient_choices.length === 0 ? (
          <p className="text-xs text-muted-foreground">No people yet.</p>
        ) : (
          <ul className="flex max-h-40 flex-col gap-1.5 overflow-y-auto pl-6">
            {view.recipient_choices.map((p) => {
              const on = chosen.includes(p.membership_id);
              return (
                <li key={p.membership_id} className="flex items-center gap-2">
                  <Checkbox
                    id={`${id}-${p.membership_id}`}
                    checked={on}
                    disabled={disabled}
                    onCheckedChange={(v) => {
                      const next =
                        v === true
                          ? [...chosen, p.membership_id]
                          : chosen.filter((x) => x !== p.membership_id);
                      // No one left: the reporting line, shown as such (never a silent fallback).
                      onChange(next.length ? next : null);
                    }}
                  />
                  <Label htmlFor={`${id}-${p.membership_id}`} className="font-normal">
                    {p.name}
                  </Label>
                </li>
              );
            })}
          </ul>
        )
      ) : null}
      {view.recipient_schedules.length ? (
        <ul className="flex flex-col gap-1 text-xs" data-slot="brief-recipient-schedules">
          {view.recipient_schedules.map((s) => (
            <li key={s.person.membership_id} className="flex justify-between gap-3">
              <span>{s.person.name}</span>
              <span className="text-muted-foreground">
                {briefSummary(s.effective)}
                {s.next_at ? ` · Next ${nextLabel(s.next_at, view.time_zone)}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
